import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Mail } from "lucide-react";
import { API } from "@/lib/api";

const FEATURE_LABELS = {
  einvoice: "E-Rechnung (ZUGFeRD & XRechnung)",
  datev: "DATEV-Export & Steuer/ELSTER",
  dashboard: "Zahlungs-Dashboard",
  deposits: "Anzahlungen & Schlussrechnungen",
  templates: "Mehrere Vorlagen & Layouts",
  dach: "DE · AT · CH inkl. Swiss QR",
  mahnlauf: "Automatischer Mahnlauf",
  multi_user: "Mehrere Benutzer / Teamverwaltung",
  priority: "Priority Support",
};

// Optionale direkte Ablefy-Kauflinks je Tarif (leer = Stripe-Checkout)
const ABLEFY_LINKS = {
  starter: "https://myablefy.com/s/femmesmarketingstudio/angebots-rechnungsstudio-75ed5a6e/payment",
  pro: "https://myablefy.com/s/femmesmarketingstudio/angebots-rechnungsstudio-angebote-schreiben-rechnungen-stellen-schneller-bezahlt-werden-3d26c7c7/payment",
  business: "https://myablefy.com/s/femmesmarketingstudio/angebots-rechnungsstudio-angebote-schreiben-rechnungen-stellen-schneller-bezahlt-werden-a98a05cf/payment",
};

function PlanSelection({ onLogout, onReload, onBack }) {
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [billingInterval, setBillingInterval] = useState("month");
  const [loadingPlan, setLoadingPlan] = useState("");
  const [rechecking, setRechecking] = useState(false);
  useEffect(() => {
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data)).catch(() => {});
    axios.get(`${API}/billing/modules`).then((r) => setModules(r.data || [])).catch(() => {});
    axios.get(`${API}/billing/bundles`).then((r) => setBundles((r.data || []).filter((bundle) => bundle.checkout_available))).catch(() => {});
  }, []);
  // Automatischer Recheck: fängt einen verspäteten Ablefy-Webhook ab, ohne dass der Käufer etwas tun muss.
  useEffect(() => {
    let n = 0;
    const t = setInterval(() => {
      n += 1;
      onReload && onReload();
      if (n >= 5) clearInterval(t);
    }, 4000);
    return () => clearInterval(t);
  }, [onReload]);
  const recheckAccess = async () => {
    setRechecking(true);
    try {
      const result = await axios.post(`${API}/billing/reconcile-ablefy`);
      await onReload?.();
      if (result.data?.subscription?.active) {
        toast.success("Dein Ablefy-Kauf wurde erkannt. Dein Zugang ist jetzt freigeschaltet.");
      } else {
        toast.message("Noch kein offener Ablefy-Kauf für diese E-Mail gefunden. Bitte die Kauf-E-Mail verwenden oder kurz erneut prüfen.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Der Ablefy-Kauf konnte gerade nicht geprüft werden.");
    } finally {
      setTimeout(() => setRechecking(false), 1500);
    }
  };
  const choose = async (planId) => {
    const ablefy = ABLEFY_LINKS[planId];
    if (ablefy) {
      window.open(ablefy, "_blank", "noopener,noreferrer");
      return;
    }
    setLoadingPlan(planId);
    try {
      const r = await axios.post(`${API}/billing/checkout`, {
        plan: planId, interval: billingInterval, origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch {
      toast.error("Checkout konnte nicht gestartet werden");
      setLoadingPlan("");
    }
  };
  const chooseBundle = async (bundleId) => {
    setLoadingPlan(`bundle:${bundleId}`);
    try {
      const r = await axios.post(`${API}/billing/bundle/checkout`, { bundle: bundleId, interval: billingInterval, origin_url: window.location.origin });
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout konnte nicht gestartet werden");
      setLoadingPlan("");
    }
  };
  return (
    <div className="min-h-screen bg-[var(--brand-bg)]" data-testid="plan-selection">
      <div className="px-6 lg:px-12 py-5 flex items-center justify-between max-w-5xl mx-auto">
        <div className="leading-tight">
          <div className="font-[Playfair_Display] text-xl font-bold text-[var(--brand-ink)]">Business Studio Suite</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-muted)]">Tarif wählen</div>
        </div>
        <button onClick={onLogout} className="text-sm text-[var(--brand-muted)] hover:text-[var(--brand-ink)] hover:underline" data-testid="plan-logout">Abmelden</button>
      </div>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
        <h1 className="font-[Playfair_Display] text-3xl sm:text-4xl font-bold text-center text-[var(--brand-ink)] mb-3">Wähle deinen Tarif</h1>
        <p className="text-center text-[var(--brand-muted)] mb-6">Um das Business Studio Suite zu nutzen, wähle bitte ein Paket. Jederzeit kündbar.</p>
        <div className="flex items-center justify-center gap-3 mb-10">
          <button onClick={() => setBillingInterval("month")} className={`px-4 py-2 rounded-full text-sm font-medium border ${billingInterval === "month" ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[var(--brand-ink)] border-[var(--brand-border)]"}`} data-testid="interval-month">Monatlich</button>
          <button onClick={() => setBillingInterval("year")} className={`px-4 py-2 rounded-full text-sm font-medium border ${billingInterval === "year" ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[var(--brand-ink)] border-[var(--brand-border)]"}`} data-testid="interval-year">Jährlich <span className="text-xs opacity-80">(2 Monate gratis)</span></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {plans.map((p) => {
            const highlight = p.id === "pro";
            return (
              <div key={p.id} className={`p-6 rounded-2xl bg-white border ${highlight ? "border-[var(--brand-primary)] shadow-lg relative" : "border-[var(--brand-border)]"}`} data-testid={`plan-card-${p.id}`}>
                {highlight && (<span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--brand-primary)] text-white text-xs px-3 py-1 rounded-full">Beliebt</span>)}
                <h3 className="font-semibold text-lg text-[var(--brand-ink)]">{p.name}</h3>
                <div className="my-3"><span className="text-4xl font-bold text-[var(--brand-ink)]">{billingInterval === "month" ? p.month : p.year} €</span><span className="text-[var(--brand-muted)]">{billingInterval === "month" ? "/Monat" : "/Jahr"}</span></div>
                <p className="text-xs text-[var(--brand-muted)] mb-4">zzgl. MwSt.</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> Angebote & Rechnungen, Online-Zahlung</li>
                  {p.features.map((f) => (<li key={f} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {FEATURE_LABELS[f] || f}</li>))}
                </ul>
                <Button onClick={() => choose(p.id)} disabled={!!loadingPlan} className={`w-full rounded-full ${highlight ? "bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" : "bg-[var(--brand-ink)] hover:bg-black text-white"}`} data-testid={`choose-${p.id}`}>
                  {loadingPlan === p.id ? "Weiterleitung…" : "Auswählen"}
                </Button>
              </div>
            );
          })}
        </div>
        {bundles.length > 0 && (
          <div className="mt-12" data-testid="plan-bundles-section">
            <h2 className="font-[Playfair_Display] text-2xl font-bold text-center text-[var(--brand-ink)] mb-2">Branchen-Bundles</h2>
            <p className="text-center text-sm text-[var(--brand-muted)] mb-6">Zwei bestehende Studios gemeinsam buchen – ohne neues Modul.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {bundles.map((bundle) => {
                const annual = billingInterval === "year";
                const amount = annual ? bundle.year : bundle.month;
                const available = annual ? bundle.checkout_available_year : bundle.checkout_available_month;
                return (
                  <div key={bundle.id} className="p-6 rounded-2xl bg-white border border-[var(--brand-primary)] shadow-sm" data-testid={`bundle-card-${bundle.id}`}>
                    <span className="inline-block mb-3 rounded-full bg-[var(--brand-primary)] px-3 py-1 text-xs font-semibold text-white">Bundle</span>
                    <h3 className="font-semibold text-lg text-[var(--brand-ink)]">{bundle.name}</h3>
                    <div className="my-3"><span className="text-4xl font-bold text-[var(--brand-ink)]">{amount.toLocaleString("de-DE")} €</span><span className="text-[var(--brand-muted)]">{annual ? "/Jahr" : "/Monat"}</span></div>
                    <p className="text-xs text-[var(--brand-muted)] mb-4">zzgl. MwSt.{annual ? " · 2 Monate gratis" : ""}</p>
                    <p className="mb-4 text-sm text-[var(--brand-muted)]">{bundle.description}</p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> Angebots- &amp; Rechnungsstudio Business</li>
                      <li className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> CRM Studio Business</li>
                    </ul>
                    <Button onClick={() => chooseBundle(bundle.id)} disabled={!!loadingPlan || !available} className="w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid={`choose-bundle-${bundle.id}`}>
                      {loadingPlan === `bundle:${bundle.id}` ? "Weiterleitung…" : !available ? "Für diese Abrechnung noch nicht verfügbar" : "Werkstatt-Bundle auswählen"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {modules.length > 0 && (
          <div className="mt-12" data-testid="plan-modules-section">
            <h2 className="font-[Playfair_Display] text-2xl font-bold text-center text-[var(--brand-ink)] mb-2">Zusätzliche Module</h2>
            <p className="text-center text-sm text-[var(--brand-muted)] mb-6">Nach der Tarifwahl jederzeit unter Einstellungen → „Module &amp; Add-ons" dazubuchbar.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {modules.map((m) => (
                <div key={m.id} className="p-6 rounded-2xl bg-white border border-[var(--brand-border)]" data-testid={`module-teaser-${m.id}`}>
                  <h3 className="font-semibold text-[var(--brand-ink)]">{m.name}</h3>
                  <p className="text-sm text-[var(--brand-muted)] mt-1">
                    ab {m.tiers[0] ? m.tiers[0].month.toLocaleString("de-DE") : "–"} €/Monat · Pakete: {m.tiers.map((t) => t.name).join(" / ")}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-center text-xs text-[var(--brand-muted)] mt-8">Sichere Zahlung über Ablefy · jederzeit kündbar.</p>
        <div className="mt-6 text-center">
          <p className="text-sm text-[var(--brand-muted)] mb-2">Bereits über Ablefy gekauft? Dein Werkstatt-Bundle wird nach dem Kauf direkt freigeschaltet. Falls die Rückmeldung verspätet ist, prüfe den Zugang hier mit derselben Kauf-E-Mail.</p>
          <button onClick={recheckAccess} disabled={rechecking} className="text-sm font-medium text-[var(--brand-primary)] hover:underline disabled:opacity-60" data-testid="recheck-access-btn">
            {rechecking ? "Zugang wird geprüft…" : "Zugang aus Ablefy-Kauf prüfen"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { PlanSelection };
